import { LoadingOutlined } from '@ant-design/icons'
import { BRANDS_WITH_REQUIRED_FREIGHT } from '@cannect/utils/constants'
import LoadingContainer from 'components/LoadingContainer'
import format from 'date-fns/format'
import { Button } from 'design-cannect'
import Heading from 'new-components/Heading'
import { AwbsData, PackagesData } from 'pages/Account/components/pages/MyOrders/types'
import { useEffect, useState } from 'react'
import {
  BsCreditCard2Back,
  BsFileEarmarkText,
  BsListCheck,
  BsPatchCheck,
  BsPatchCheckFill,
  BsSearch,
  BsTag,
  BsTruck
} from 'react-icons/bs'
import { HiOutlineDocumentCheck } from 'react-icons/hi2'
import { SlSocialDropbox } from 'react-icons/sl'
import { useHistory, useLocation } from 'react-router'
import { toast } from 'react-toastify'
import api from 'services/api'
import ProductList from '../Product'
import { Resume } from '../Resume'
import PaymentDropdown from './components/PaymentDropdown'
import * as S from './styles'

// Constantes para marcas que não precisam de inspeção ANVISA
const BRANDS_WITH_POPUP = [177] // Mostram pop-up do pacote enviado

interface OrderStatusType {
  color: string
  date: string
  status: string
  translated_name: string
}

// Steps alternativos para pedidos 100% TESTE_GENETICO
const GENETIC_TEST_STATUS = [
  {
    id: 'gt_1',
    idName: ['awaiting_payment', 'paid'],
    name: 'Criado',
    checkedName: 'Pagamento aprovado',
    activeName: 'Aguardando pagamento',
    icon: <BsCreditCard2Back />,
    nextStepName: ['sented', 'dispatched']
  },
  {
    id: 'gt_2',
    idName: ['sented', 'dispatched'],
    name: 'Enviado',
    checkedName: 'Enviado',
    activeName: 'Enviado',
    icon: <BsTruck />,
    nextStepName: ['kit_activated']
  },
  {
    id: 'gt_3',
    idName: ['kit_activated'],
    name: 'Kit ativado',
    checkedName: 'Kit ativado',
    activeName: 'Kit ativado',
    icon: <BsListCheck />,
    nextStepName: ['received']
  },
  {
    id: 'gt_4',
    idName: ['received'],
    name: 'Recebido',
    checkedName: 'Recebido',
    activeName: 'Recebido',
    icon: <SlSocialDropbox />,
    nextStepName: ['under_review']
  },
  {
    id: 'gt_5',
    idName: ['under_review'],
    name: 'Em análise',
    checkedName: 'Em análise',
    activeName: 'Em análise',
    icon: <BsSearch />,
    nextStepName: ['partially_completed', 'completed']
  },
  {
    id: 'gt_6',
    idName: ['partially_completed'],
    name: 'Concluído Parcialmente',
    checkedName: 'Concluído Parcialmente',
    activeName: 'Concluído Parcialmente',
    icon: <BsPatchCheck />,
    nextStepName: ['completed']
  },
  {
    id: 'gt_7',
    idName: ['completed'],
    name: 'Concluído',
    checkedName: 'Concluído',
    activeName: 'Concluído',
    icon: <BsPatchCheckFill />,
    nextStepName: []
  }
]

function buildGeneticTestTimeline(statusLogs: any[], paymentStatus: any) {
  const allLaterIdNames = (fromIndex: number) => GENETIC_TEST_STATUS.slice(fromIndex + 1).flatMap((s) => s.idName)

  return GENETIC_TEST_STATUS.map((step, index) => {
    let statusCreatedAt = ''
    let shouldBeChecked = false
    let itemLabel = step.name

    if (step.id === 'gt_1') {
      const isPaid = paymentStatus?.status === 'paid'
      shouldBeChecked = isPaid
      itemLabel = isPaid ? 'Pagamento aprovado' : 'Aguardando pagamento'
      if (isPaid) statusCreatedAt = paymentStatus?.createdAt || ''
    } else {
      const logsForStep = (statusLogs || []).filter((log: any) => step.idName.includes(log.status?.name))
      const hasLaterStep = (statusLogs || []).some((log: any) => allLaterIdNames(index).includes(log.status?.name))

      if (hasLaterStep) {
        shouldBeChecked = true
        const latestLog = [...logsForStep].sort(
          (a: any, b: any) =>
            new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime()
        )[0]
        statusCreatedAt = latestLog?.updatedAt || latestLog?.createdAt || ''
      } else if (logsForStep.length > 0) {
        const isLastStep = allLaterIdNames(index).length === 0
        shouldBeChecked = isLastStep
        const latestLog = [...logsForStep].sort(
          (a: any, b: any) =>
            new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime()
        )[0]
        statusCreatedAt = latestLog?.updatedAt || latestLog?.createdAt || ''
      }
    }

    return {
      ...step,
      checked: shouldBeChecked,
      isVerified: shouldBeChecked,
      label: itemLabel,
      statusCreatedAt,
      message: ''
    }
  })
}

const status = [
  {
    id: 1,
    idName: ['awaiting_payment', 'paid'],
    name: 'Status do pagamento.',
    checkedName: 'Pagamento aprovado',
    activeName: 'Aguardando pagamento',
    icon: <BsCreditCard2Back />,
    checked: true,
    isVerified: true,
    label: 'Aguardando pagamento',
    nextStepName: ['waiting_documentation']
  },
  {
    id: 2,
    idName: ['waiting_documentation', 'waiting_approval', 'waiting_anvisa', 'disapproved_document'],
    name: 'Validações de documentos.',
    checkedName: 'Documentos aprovados',
    activeName: 'Documentos em validação',
    icon: <HiOutlineDocumentCheck />,
    checked: false,
    isVerified: false,
    label: 'Documentos aprovados',
    nextStepName: ['approved_documents']
  },
  {
    id: 4,
    idName: [
      'dispatched',
      'created',
      'approved_documents',
      'approved',
      'waiting_separation',
      'in_separation',
      'preparing_shipping',
      'separated',
      'ready_shipping',
      'waiting_stock',
      'sented',
      'shipping_supplier'
    ],
    name: 'Status do pacote',
    checkedName: 'Pacote enviado',
    activeName: 'Pacote em preparação',
    icon: <SlSocialDropbox />,
    checked: false,
    isVerified: false,
    label: 'Pacote enviado',
    nextStepName: ['dispatched', 'sented']
  },
  {
    id: 6,
    idName: [
      'anvisa_exception',
      'delay_release',
      'anvisa_br',
      'federal_revenue',
      'federal_revenue_analysis',
      'Irregular_CPF',
      'regular_CPF',
      'returning_origin'
    ],
    name: 'Inspeção ANVISA',
    checkedName: 'Aprovado pela ANVISA',
    activeName: 'Em inspeção ANVISA',
    icon: <BsSearch />,
    checked: false,
    isVerified: false,
    label: 'Inspeção ANVISA',
    nextStepName: ['release_anvisa']
  },
  {
    id: 7,
    idName: ['release_anvisa'],
    name: 'Liberado pela ANVISA',
    checkedName: 'Liberado pela ANVISA',
    activeName: 'Aguardando liberação',
    icon: <BsListCheck />,
    checked: false,
    isVerified: false,
    label: 'Liberado pela ANVISA',
    nextStepName: ['delivered', 'in_transit', 'not_delivered', 'out_for_delivery']
  },
  {
    id: 8,
    idName: [
      'delivered',
      'in_transit',
      'not_delivered',
      'out_for_delivery',
      'delivery_attempt',
      'package_held',
      'damaged_package',
      'problems_with_delivery'
    ],
    name: 'Pedido entregue',
    checkedName: 'Pedido Entregue',
    activeName: 'Aguardando Entrega',
    icon: <BsPatchCheck />,
    checked: false,
    isVerified: false,
    label: 'Pedido entregue',
    nextStepName: ['delivered']
  }
]

function AnalyzeCannect() {
  const [orderDetails, setOrderDetails] = useState<any>({ items: [] })
  const [PackageDetails, setPackageDetails] = useState<any>({ packages: [] })
  const [orderStatus, setOrderStatus] = useState({} as OrderStatusType)
  const [statusPayment, setStatusPayment] = useState({} as OrderStatusType)
  const [timeLineStatus, setTimeLineStatus] = useState(status)
  const [loading, setLoading] = useState(true)
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const history = useHistory()
  const [activeStepIndex, setActiveStepIndex] = useState<number | null>(null)
  const [OrderPackage, setOrderPackage] = useState<PackagesData[]>([])
  const [OrderAwb, setOrderAwb] = useState<AwbsData[]>([])
  const [PackageCount, setPackageCount] = useState<number>(0)
  const isSmallScreen = window.innerWidth < 750
  const [Data, SetData] = useState<any>({})
  const [StatusMessage, setStatusMessage] = useState<string>('')
  const [DocumentExcept, setDocumentExcept] = useState<boolean>(false)
  const [StatusIndex, setStatusIndex] = useState<number>(0)
  const [paymentInfo, setPaymentInfo] = useState<any>(null)
  const [loadingPayment, setLoadingPayment] = useState(true)
  const [charges, setCharges] = useState([])
  const [isDeliveredByAssociation, setIsDeliveredByAssociation] = useState(false)
  const cart_id = new URLSearchParams(useLocation().search).get('cart_id')
  const linkAtendimento =
    'https://api.whatsapp.com/send/?phone=5511982711992&text=Oi%2C+gostaria+de+iniciar+meu+tratamento%21&type=phone_number&app_absent=0'

  // Seleciona o log mais recente considerando updatedAt (fallback em createdAt)
  const getLatestByUpdated = (logs: any[] | undefined) => {
    if (!Array.isArray(logs) || logs.length === 0) return undefined
    return logs.reduce((latest: any, log: any) => {
      if (!latest) return log
      const latestUp = new Date(log?.updatedAt || log?.createdAt).getTime()
      const currentUp = new Date(latest?.updatedAt || latest?.createdAt).getTime()
      return latestUp > currentUp ? log : latest
    })
  }

  // Função para gerar mensagem descritiva baseada no status e step
  const getStatusMessage = (statusName: string, stepId: number, orderData?: any) => {
    let message = ''

    if (stepId === 1) {
      message = 'Pagamento confirmado'
    }

    if (stepId === 2) {
      if (statusName === 'waiting_documentation') message = 'Aguardando envio dos documentos'
      if (statusName === 'waiting_approval')
        message = 'Recebemos todos os seus documentos, agora estamos gerando sua autorização Anvisa'
      if (statusName === 'waiting_anvisa')
        message = 'Recebemos todos os seus documentos, em breve entraremos em contato com mais informações'
      if (statusName === 'disapproved_document') message = 'Houve um problema com seus documentos'
      if (statusName === 'approved_documents') message = 'Tudo certo com seus documentos'
    }

    if (stepId === 4) {
      if (statusName === 'waiting_stock')
        message = 'Seu pedido está em etapa de preparação logística e em breve seguirá para envio.'
      if (
        statusName === 'created' ||
        statusName === 'approved' ||
        statusName === 'approved_documents' ||
        statusName === 'waiting_separation'
      ) {
        message = 'Seu pacote foi enviado para separação'
      }
      if (statusName === 'in_separation') message = 'Seu pacote está sendo separado'
      if (statusName === 'preparing_shipping' || statusName === 'separated' || statusName === 'ready_shipping') {
        message = 'Seu pacote foi embalado. Em breve será enviado para transportadora'
      }
      if (statusName === 'dispatched' || statusName === 'sented' || statusName === 'shipping_supplier') {
        message = 'Seu pacote já foi enviado.'
      }
    }

    if (stepId === 6 || stepId === 7) {
      if (statusName === 'anvisa_br' || statusName === 'anvisa_exception' || statusName === 'delay_release') {
        message = 'Seu pacote está em análise pela ANVISA.'
      }
      if (statusName === 'release_anvisa') {
        message = 'Seu pacote foi liberado pela ANVISA. Em breve entrará em rota de entrega'
      }
    }

    if (stepId === 8) {
      if (statusName === 'in_transit' || statusName === 'not_delivered') {
        message = orderData?.street
          ? `Seu pacote está em trânsito. Destino: ${orderData.street}, ${orderData.address_number}`
          : 'Seu pacote está em trânsito'
      }
      if (statusName === 'out_for_delivery') {
        message = orderData?.street
          ? `Seu pacote saiu para entrega. Destino: ${orderData.street}, ${orderData.address_number}`
          : 'Seu pacote saiu para entrega'
      }
      if (statusName === 'delivered') message = 'Seu pedido foi entregue'
    }

    return message
  }

  const getMostAdvancedLog = (logs: any[], stepIdNames: string[]): any => {
    if (!logs || logs.length === 0) return null

    const relevantLogs = logs.filter((log: any) => stepIdNames.includes(log.status?.name))
    if (relevantLogs.length === 0) return null

    return relevantLogs.reduce((mostAdvanced: any, log: any) => {
      if (!mostAdvanced) return log

      const currentIndex = stepIdNames.indexOf(log.status?.name)
      const mostAdvancedIndex = stepIdNames.indexOf(mostAdvanced.status?.name)

      if (currentIndex < mostAdvancedIndex && currentIndex !== -1) {
        return log
      }
      return mostAdvanced
    }, null)
  }

  const getLastOrders = async (loading = true) => {
    setLoading(loading)
    setLoadingPayment(true)
    try {
      const { data } = await api.get(`/order_b2c_public/${cart_id}`)

      if (data?.order) {
        const responsePackage = await api.get(`/packages/${data?.order?.id}/package-with-status`)
        const statusIndex = status.findIndex((item) => item.idName.includes(data?.order?.status?.order_status?.status))
        const latestLog = getLatestByUpdated(responsePackage?.data?.packages[PackageCount]?.statusLogs)
        let orderStatus =
          statusIndex > 1 ? latestLog?.status || data?.order?.status?.order_status : data?.order?.status?.order_status

        const activeStepIndex =
          statusIndex > 1
            ? data?.order?.packages[PackageCount].statusLogs
              ? status.findIndex((item) => item.idName.includes(orderStatus.name))
              : status.findIndex((item) => item.idName.includes(orderStatus?.status))
            : status.findIndex((item) => item.idName.includes(orderStatus?.status))

        const packageDetails = responsePackage?.data?.packages
        let payment_status: OrderStatusType = {
          status: 'waiting_payment',
          translated_name: 'Aguardando pagamento',
          date: data?.order?.payments[0]?.createdAt,
          color: '#FFD700'
        }

        if (data?.order?.payments.length > 0) {
          const { data: payments } = await api.get(`/payments/info/${data?.order.id}`)

          setPaymentInfo(payments.payment)
          const paidPayment = payments.payment.find((payment: any) => payment.status === 'paid')

          if (paidPayment) {
            payment_status = {
              status: 'paid',
              translated_name: 'Pagamento aprovado',
              date: paidPayment.createdAt,
              color: '#0D855C'
            }
          } else {
            const firstPayment = payments.payment[0]
            payment_status = {
              status: firstPayment?.status || 'waiting_payment',
              translated_name: 'Aguardando pagamento',
              date: firstPayment?.createdAt || data?.order?.payments[0]?.createdAt,
              color: '#FFD700'
            }
          }
        }

        const OrderPackage = packageDetails.length === 0 ? data?.order?.packages : packageDetails
        orderStatus = {
          ...orderStatus,
          statusData: Array.isArray(packageDetails[PackageCount]?.statusLogs)
            ? getLatestByUpdated(packageDetails[PackageCount]?.statusLogs)?.updatedAt
            : data?.order?.status?.order_status?.updatedAt
        }

        // Verifica se todos os produtos não precisam de ANVISA
        const currentPackageItems = packageDetails[PackageCount]?.packages_items || []
        const currentPackageSupplierId = currentPackageItems[0]?.supplier_id

        // Verifica produtos que não precisam de ANVISA por delivery_by_association, class_id ou supplier
        const allProductsDeliveredByAssociation = currentPackageItems.every(
          (packageItem: any) =>
            packageItem.product?.delivery_by_association === true ||
            packageItem.class_id === 4 ||
            packageItem.class_id === 6
        )

        // Verifica produtos que não precisam de ANVISA por brand_id
        const allProductsWithSpecialBrand = currentPackageItems.every(
          (packageItem: any) =>
            BRANDS_WITH_REQUIRED_FREIGHT.includes(packageItem.brand_id) ||
            BRANDS_WITH_REQUIRED_FREIGHT.includes(packageItem.product?.brand_id)
        )

        // Verifica se todos os produtos têm brand_id que deve mostrar pop-up (apenas 177)
        const allProductsWithPopupBrand = currentPackageItems.every(
          (packageItem: any) =>
            BRANDS_WITH_POPUP.includes(packageItem.brand_id) ||
            BRANDS_WITH_POPUP.includes(packageItem.product?.brand_id)
        )

        // Combina todas as condições para remover ANVISA
        const allProductsDontNeedAnvisa = allProductsDeliveredByAssociation || allProductsWithSpecialBrand

        // Verifica se todos os produtos têm class_id === 6 (não precisam de documentos aprovados)
        const allProductsClassId6 = currentPackageItems.every((packageItem: any) => packageItem.class_id === 6)

        // Verifica se todos os itens do pacote (ou do pedido se sem pacote) são TESTE_GENETICO
        const itemsToCheck = currentPackageItems.length > 0 ? currentPackageItems : data?.order?.items || []
        const allProductsGeneticTest =
          itemsToCheck.length > 0 &&
          itemsToCheck.every((item: any) => (item.product_type || item.product?.type || item.type) === 'TESTE_GENETICO')

        if (allProductsGeneticTest) {
          const paidPayment = data?.order?.payments?.find((payment: any) => payment.status === 'paid')
          const pkgLogs = packageDetails[PackageCount]?.statusLogs
          const logsArray = Array.isArray(pkgLogs) ? pkgLogs : []
          setTimeLineStatus(buildGeneticTestTimeline(logsArray, paidPayment))
          setPackageDetails(packageDetails)
          messageDefine(orderStatus, data?.order)
          setOrderStatus(orderStatus)
          setOrderDetails(data?.order)
          setStatusPayment(payment_status)
          setStatusIndex(statusIndex)
          setOrderPackage(OrderPackage)
          SetData(data)
          return
        }

        // Filtra steps que não são necessários
        let stepsToProcess = status.map((step) => ({ ...step })) // Clone para não modificar o original

        // Remove step 2 (Documentos aprovados) se todos os produtos têm class_id === 6
        if (allProductsClassId6) {
          stepsToProcess = stepsToProcess.filter((step) => step.id !== 2)
        }

        // Remove steps 6 e 7 (ANVISA) se não for necessário
        if (allProductsDontNeedAnvisa || currentPackageSupplierId == 18) {
          stepsToProcess = stepsToProcess.filter((step) => step.id !== 6 && step.id !== 7)

          // Atualiza nextStepName do step 4 para ir direto para entrega
          const step4Index = stepsToProcess.findIndex((s) => s.id === 4)
          if (step4Index !== -1) {
            stepsToProcess[step4Index] = {
              ...stepsToProcess[step4Index],
              nextStepName: ['in_transit', 'delivered', 'out_for_delivery', 'not_delivered']
            }
          }
        }

        // Recalcula activeStepIndex baseado nos steps filtrados
        let filteredActiveStepIndex = stepsToProcess.findIndex(
          (item) => item.idName.includes(orderStatus.name) || item.idName.includes(orderStatus.status)
        )

        // Se não encontrou no idName, verifica se está no nextStepName (status de conclusão)
        // Nesse caso, o step ativo é o próximo
        if (filteredActiveStepIndex === -1) {
          const completedStepIndex = stepsToProcess.findIndex(
            (item) => item.nextStepName.includes(orderStatus.name) || item.nextStepName.includes(orderStatus.status)
          )
          if (completedStepIndex !== -1 && completedStepIndex < stepsToProcess.length - 1) {
            filteredActiveStepIndex = completedStepIndex + 1
          }
        }

        if (filteredActiveStepIndex >= 0) {
        }

        setTimeLineStatus(
          stepsToProcess.map((item, index) => {
            let statusCreatedAt = ''
            let shouldBeChecked = false
            let itemLabel = item.label
            let itemMessage = ''

            // STEP 1: PAGAMENTO
            if (item.id === 1) {
              const isPaid = data?.order?.payments?.some((payment: any) => payment.status === 'paid')

              if (isPaid) {
                shouldBeChecked = true
                itemLabel = 'Pagamento aprovado'
                const paidPayment = data?.order?.payments?.find((payment: any) => payment.status === 'paid')
                statusCreatedAt = paidPayment?.createdAt || ''
                itemMessage = getStatusMessage('paid', item.id, data?.order)
              } else {
                shouldBeChecked = false
                itemLabel = 'Aguardando confirmação do pagamento'
              }
            } else {
              // OUTROS STEPS: Verifica se tem log de conclusão (nextStepName)
              const completionLogs = packageDetails[PackageCount]?.statusLogs?.filter((log: any) =>
                item.nextStepName.includes(log.status.name)
              )

              if (completionLogs && completionLogs.length > 0) {
                // TEM log de conclusão - step completo
                shouldBeChecked = true
                const latestLog = completionLogs.sort(
                  (a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
                )[0]
                statusCreatedAt = latestLog.updatedAt
                itemMessage = getStatusMessage(latestLog.status?.name, item.id, data?.order)
              } else {
                // NÃO tem log de conclusão - verifica se está ativo OU se já passou desse step
                const isCurrentlyInThisStep =
                  item.idName.includes(orderStatus.name) || item.idName.includes(orderStatus.status)
                const isBeforeActiveStep = filteredActiveStepIndex >= 0 && index < filteredActiveStepIndex

                if (isCurrentlyInThisStep) {
                  // Step ATIVO - busca log do status atual
                  shouldBeChecked = false

                  // Primeiro tenta buscar logs do status atual específico
                  const currentStatusLogs = packageDetails[PackageCount]?.statusLogs?.filter(
                    (log: any) => log.status?.name === orderStatus.name || log.status?.name === orderStatus.status
                  )

                  if (currentStatusLogs && currentStatusLogs.length > 0) {
                    // Pega o log MAIS RECENTE do status atual
                    // Usa updatedAt se existir, senão createdAt
                    const newestLog = currentStatusLogs.sort((a: any, b: any) => {
                      const dateA = new Date(a.updatedAt || a.createdAt).getTime()
                      const dateB = new Date(b.updatedAt || b.createdAt).getTime()
                      return dateB - dateA
                    })[0]
                    statusCreatedAt = newestLog.updatedAt || newestLog.createdAt
                    itemMessage = getStatusMessage(orderStatus.name || orderStatus.status, item.id, data?.order)
                  } else {
                    // Fallback: usa data do orderStatus se não tem logs
                    statusCreatedAt = orderStatus?.statusData || data?.order?.status?.order_status?.updatedAt || ''
                  }
                } else if (isBeforeActiveStep) {
                  // Step ANTERIOR ao ativo - marca como completo mesmo sem log específico
                  shouldBeChecked = true
                  // Busca o log mais AVANÇADO do step (não o mais recente por data)
                  const logsInStep = packageDetails[PackageCount]?.statusLogs?.filter((log: any) =>
                    item.idName.includes(log.status.name)
                  )
                  if (logsInStep && logsInStep.length > 0) {
                    // Usa a função getMostAdvancedLog para pegar o status mais avançado na progressão
                    const mostAdvancedLog = getMostAdvancedLog(logsInStep, item.idName)
                    if (mostAdvancedLog) {
                      statusCreatedAt = mostAdvancedLog.updatedAt || mostAdvancedLog.createdAt
                      itemMessage = getStatusMessage(mostAdvancedLog.status?.name, item.id, data?.order)
                    }
                  }
                } else {
                  // Step FUTURO - não está completo nem ativo
                  shouldBeChecked = false
                  statusCreatedAt = ''
                }
              }
            }

            // Verifica se deve mostrar o infoContent
            // Só mostra se o step estiver ativo (tem statusCreatedAt) ou completo (checked)
            const isStepActiveOrComplete = shouldBeChecked || !!statusCreatedAt

            let shouldShowInfoContent = isStepActiveOrComplete
            // Lógica do pop-up do "Pacote enviado" (step 4):
            // - Oculta se: supplier_id 18, delivery_by_association, class_id 4/6, ou brand_id 173
            // - Mostra se: brand_id 177
            if (item.id === 4) {
              const shouldHidePopup =
                currentPackageSupplierId == 18 ||
                allProductsDeliveredByAssociation ||
                (allProductsWithSpecialBrand && !allProductsWithPopupBrand) // brand_id 173

              if (shouldHidePopup) {
                shouldShowInfoContent = false
              }
            }

            return {
              ...item,
              checked: shouldBeChecked,
              isVerified: shouldBeChecked,
              label: itemLabel,
              statusCreatedAt,
              message: itemMessage,
              infoContent: shouldShowInfoContent ? item.infoContent : undefined
            }
          })
        )

        setPackageDetails(packageDetails)
        messageDefine(orderStatus, data?.order)
        setOrderStatus(orderStatus)
        setOrderDetails(data?.order)
        setStatusPayment(payment_status)
        setActiveStepIndex(filteredActiveStepIndex) // Usa o índice filtrado, não o original
        setStatusIndex(statusIndex)

        const currentPackage = PackageDetails[PackageCount] || data?.order?.packages[PackageCount]

        const isDeliveredByAssociation =
          currentPackage?.delivered_by_association ||
          data?.order?.delivered_by_association ||
          PackageDetails[PackageCount]?.delivered_by_association ||
          false

        setIsDeliveredByAssociation(isDeliveredByAssociation)

        if (data?.order?.packages[PackageCount]?.statusLogs) {
          setOrderPackage(OrderPackage)
        } else {
          setOrderAwb(OrderPackage)
        }
        setLoadingPayment(false)
        SetData(data)
      }
    } catch (e) {
      console.log(e)
      toast.error('Erro na busca dos detalhes do pedido')
      setLoadingPayment(false)
    } finally {
      setLoading(false)
      setLoadingPayment(false)
    }
  }

  useEffect(() => {
    if (cart_id) {
      getLastOrders()
    }
  }, [cart_id])

  useEffect(() => {
    const paidPayment = paymentInfo && paymentInfo.find((payment) => payment?.status === 'paid')

    if (paidPayment && paidPayment.payments_orders) {
      const { payments_orders } = paidPayment
      setCharges(payments_orders.charges)
    }
  }, [paymentInfo])

  if (loading) return <LoadingContainer loading />

  const getStatusCss = (checked: boolean, isVerified: boolean, isPending: boolean) => {
    if (isPending) {
      return 'pending'
    }
    if (checked && isVerified) {
      return 'success'
    }
    if (!checked && isVerified) {
      return 'danger'
    }
    return 'default'
  }

  function messageDefine(packageStatus: any, orderData: any) {
    const status = packageStatus?.translated_name

    let message = ''

    if (orderData.payments.length > 0 && orderData.payments.some((payment: any) => payment.status === 'paid')) {
      if (status === 'Aguardando Documentos') message = 'Você ainda não encaminhou todos os documentos.'
      if (status === 'Aguardando Anvisa')
        message = 'Recebemos todos os seus documentos, agora estamos gerando sua autorização Anvisa'
      if (status === 'Aguardando Aprovação')
        message = 'Recebemos todos os seus documentos, em breve entraremos em contato com mais informações.'
      if (status === 'Documento reprovado') message = 'Houve um problema com seus documentos.'
      if (status === 'Documentos Aprovados') message = 'Tudo certo com seus documentos.'
      if (status === 'Aguardando estoque')
        message = 'Seu pedido está em etapa de preparação logística e em breve seguirá para envio.'
      if (
        status === 'Criado' ||
        status === 'Aprovado' ||
        status === 'Preparando Envio' ||
        status === 'Aguardando separação'
      )
        message = 'Seu pacote foi enviado para separação.'
      if (status === 'Em separação') message = 'Seu pacote está sendo separado.'
      if (status === 'Separado' || status === 'Pronto para envio' || status === 'Aguardando Envio')
        message = 'Seu pacote foi embalado. Em breve será enviado para transportadora.'
      if (status === 'Enviado' || status === 'Enviado') message = 'Seu pacote já foi enviado.'
      if (status === 'Anvisa BR' || status === 'Exceção Anvisa' || status === 'Análise Anvisa')
        message = 'Seu pacote está em análise pela ANVISA.'
      if (status === 'Liberado Anvisa')
        message = 'Seu pacote foi liberado pela anvisa. Em breve entrará em rota de entrega.'
      if (status === 'Em trânsito' || status === 'Não entregue')
        message = `Seu pacote está em trânsito. Destino: ${orderData?.street}, ${orderData?.address_number}`
      if (status === 'Saiu para entrega')
        message = `Seu pacote saiu para entrega. Destino: ${orderData?.street}, ${orderData?.address_number}`
      if (status === 'Entregue') message = 'Seu pedido foi entregue no dia'

      setStatusMessage(`${message} - ${format(new Date(packageStatus?.statusData), 'dd/MM/yyyy - HH:mm')}.`)
      if (status === 'Aguardando Documentos' || status === 'Documento reprovado') setDocumentExcept(true)
    }
  }

  function packageUpdate(packIndex: number) {
    if (getLatestByUpdated(PackageDetails[packIndex]?.statusLogs)?.status) {
      let orderStat =
        StatusIndex > 1
          ? getLatestByUpdated(PackageDetails[packIndex]?.statusLogs)?.status
          : Data?.order?.status?.order_status

      orderStat = {
        ...orderStat,
        statusData: getLatestByUpdated(PackageDetails[packIndex]?.statusLogs)?.updatedAt
      }

      // Aplica os mesmos filtros que em getLastOrders
      const currentPackageItems = PackageDetails[packIndex]?.packages_items || []
      const currentPackageSupplierId = currentPackageItems[0]?.supplier_id

      const allProductsDeliveredByAssociation = currentPackageItems.every(
        (packageItem: any) =>
          packageItem.product?.delivery_by_association === true ||
          packageItem.class_id === 4 ||
          packageItem.class_id === 6
      )

      const allProductsWithSpecialBrand = currentPackageItems.every(
        (packageItem: any) =>
          BRANDS_WITH_REQUIRED_FREIGHT.includes(packageItem.brand_id) ||
          BRANDS_WITH_REQUIRED_FREIGHT.includes(packageItem.product?.brand_id)
      )

      const allProductsDontNeedAnvisa = allProductsDeliveredByAssociation || allProductsWithSpecialBrand

      const allProductsClassId6 = currentPackageItems.every((packageItem: any) => packageItem.class_id === 6)

      // Verifica se todos os itens do pacote são TESTE_GENETICO
      const itemsToCheckPkg = currentPackageItems.length > 0 ? currentPackageItems : Data?.order?.items || []
      const allProductsGeneticTestPkg =
        itemsToCheckPkg.length > 0 &&
        itemsToCheckPkg.every(
          (item: any) => (item.product_type || item.product?.type || item.type) === 'TESTE_GENETICO'
        )

      if (allProductsGeneticTestPkg) {
        const paidPayment = Data?.order?.payments?.find((payment: any) => payment.status === 'paid')
        const pkgLogs = PackageDetails[packIndex]?.statusLogs
        const logsArray = Array.isArray(pkgLogs) ? pkgLogs : []
        setTimeLineStatus(buildGeneticTestTimeline(logsArray, paidPayment))
        setOrderStatus(orderStat)
        setPackageCount(packIndex)
        return
      }

      // Filtra steps
      let stepsToProcess = status.map((step) => ({ ...step }))

      if (allProductsClassId6) {
        stepsToProcess = stepsToProcess.filter((step) => step.id !== 2)
      }

      if (allProductsDontNeedAnvisa || currentPackageSupplierId == 18) {
        stepsToProcess = stepsToProcess.filter((step) => step.id !== 6 && step.id !== 7)

        const step4Index = stepsToProcess.findIndex((s) => s.id === 4)
        if (step4Index !== -1) {
          stepsToProcess[step4Index] = {
            ...stepsToProcess[step4Index],
            nextStepName: ['in_transit', 'delivered', 'out_for_delivery', 'not_delivered']
          }
        }
      }

      // Recalcula activeStepIndex com steps filtrados
      let activeStepIndex = stepsToProcess.findIndex(
        (item) => item.idName.includes(orderStat.name) || item.idName.includes(orderStat.status)
      )

      if (activeStepIndex === -1) {
        const completedStepIndex = stepsToProcess.findIndex(
          (item) => item.nextStepName.includes(orderStat.name) || item.nextStepName.includes(orderStat.status)
        )
        if (completedStepIndex !== -1 && completedStepIndex < stepsToProcess.length - 1) {
          activeStepIndex = completedStepIndex + 1
        }
      }

      // Usa a mesma lógica completa de processamento de getLastOrders
      setTimeLineStatus(
        stepsToProcess.map((item, index) => {
          let statusCreatedAt = ''
          let shouldBeChecked = false
          let itemLabel = item.label
          let itemMessage = ''

          if (item.id === 1) {
            const isPaid = Data?.order?.payments?.some((payment: any) => payment.status === 'paid')
            if (isPaid) {
              shouldBeChecked = true
              itemLabel = 'Pagamento aprovado'
              const paidPayment = Data?.order?.payments?.find((payment: any) => payment.status === 'paid')
              statusCreatedAt = paidPayment?.createdAt || ''
              itemMessage = getStatusMessage('paid', item.id, Data?.order)
            } else {
              shouldBeChecked = false
              itemLabel = 'Aguardando confirmação do pagamento'
            }
          } else {
            const completionLogs = PackageDetails[packIndex]?.statusLogs?.filter((log: any) =>
              item.nextStepName.includes(log.status.name)
            )

            if (completionLogs && completionLogs.length > 0) {
              shouldBeChecked = true
              const latestLog = completionLogs.sort(
                (a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
              )[0]
              statusCreatedAt = latestLog.updatedAt
              itemMessage = getStatusMessage(latestLog.status?.name, item.id, Data?.order)
            } else {
              const isCurrentlyInThisStep =
                item.idName.includes(orderStat.name) || item.idName.includes(orderStat.status)
              const isBeforeActiveStep = activeStepIndex >= 0 && index < activeStepIndex

              if (isCurrentlyInThisStep) {
                shouldBeChecked = false
                const currentStatusLogs = PackageDetails[packIndex]?.statusLogs?.filter(
                  (log: any) => log.status?.name === orderStat.name || log.status?.name === orderStat.status
                )
                if (currentStatusLogs && currentStatusLogs.length > 0) {
                  const newestLog = currentStatusLogs.sort((a: any, b: any) => {
                    const dateA = new Date(a.updatedAt || a.createdAt).getTime()
                    const dateB = new Date(b.updatedAt || b.createdAt).getTime()
                    return dateB - dateA
                  })[0]
                  statusCreatedAt = newestLog.updatedAt || newestLog.createdAt
                  itemMessage = getStatusMessage(orderStat.name || orderStat.status, item.id, Data?.order)
                } else {
                  statusCreatedAt = orderStat?.statusData || Data?.order?.status?.order_status?.updatedAt || ''
                }
              } else if (isBeforeActiveStep) {
                // Step ANTERIOR ao ativo - marca como completo mesmo sem log específico
                shouldBeChecked = true
                // Busca o log mais AVANÇADO do step (não o mais recente por data)
                const logsInStep = PackageDetails[packIndex]?.statusLogs?.filter((log: any) =>
                  item.idName.includes(log.status.name)
                )
                if (logsInStep && logsInStep.length > 0) {
                  // Usa a função getMostAdvancedLog para pegar o status mais avançado na progressão
                  const mostAdvancedLog = getMostAdvancedLog(logsInStep, item.idName)
                  if (mostAdvancedLog) {
                    statusCreatedAt = mostAdvancedLog.updatedAt || mostAdvancedLog.createdAt
                    itemMessage = getStatusMessage(mostAdvancedLog.status?.name, item.id, Data?.order)
                  }
                }
              } else {
                shouldBeChecked = false
                statusCreatedAt = ''
              }
            }
          }

          const isStepActiveOrComplete = shouldBeChecked || !!statusCreatedAt
          let shouldShowInfoContent = isStepActiveOrComplete

          if (item.id === 4) {
            const allProductsWithPopupBrand = currentPackageItems.every(
              (packageItem: any) =>
                BRANDS_WITH_POPUP.includes(packageItem.brand_id) ||
                BRANDS_WITH_POPUP.includes(packageItem.product?.brand_id)
            )
            const shouldHidePopup =
              currentPackageSupplierId == 18 ||
              allProductsDeliveredByAssociation ||
              (allProductsWithSpecialBrand && !allProductsWithPopupBrand)

            if (shouldHidePopup) {
              shouldShowInfoContent = false
            }
          }

          return {
            ...item,
            checked: shouldBeChecked,
            isVerified: shouldBeChecked,
            label: itemLabel,
            statusCreatedAt,
            message: itemMessage,
            infoContent: shouldShowInfoContent ? item.infoContent : undefined
          }
        })
      )

      setOrderStatus(orderStat)
      setActiveStepIndex(activeStepIndex)
      setPackageCount(packIndex)

      const currentPackage = PackageDetails[packIndex]

      const isDeliveredByAssociation =
        currentPackage?.delivered_by_association ||
        orderDetails?.delivered_by_association ||
        PackageDetails[packIndex]?.delivered_by_association ||
        false

      setIsDeliveredByAssociation(isDeliveredByAssociation)

      messageDefine(orderStat, orderDetails)
    }
  }

  let filteredItems = PackageDetails[PackageCount]?.statusLogs
    ? orderDetails?.items.filter((item: any) =>
        PackageDetails[PackageCount]?.packages_items.some(
          (packageItem: { product_id: number }) =>
            packageItem.product_id === (item?.product?.kit_product?.id ? item.product.kit_product.id : item.product.id)
        )
      )
    : orderDetails?.items

  const duplicatesDeleter = new Set()
  const uniqueItems = []

  for (const item of filteredItems) {
    const value = item?.product?.kit_product?.id || item?.product?.id
    if (!duplicatesDeleter.has(value)) {
      duplicatesDeleter.add(value)
      uniqueItems.push(item)
    }
  }

  if (PackageDetails[PackageCount]?.statusLogs) {
    filteredItems = uniqueItems.map((item: any) => {
      const findItem = PackageDetails[PackageCount]?.packages_items.find(
        (packageItem: any) =>
          packageItem.product_id === (item?.product?.kit_product?.id ? item.product.kit_product.id : item.product.id)
      )

      const quantity = findItem ? findItem.quantity_item_for_package : findItem.quantity
      const unitPrice = item?.product?.kit_product?.id
        ? item?.product?.kit_product?.price || item?.product?.kit_product?.real_price
        : item?.unitPrice

      const product = item?.product?.kit_product?.id ? item?.product?.kit_product : item?.product

      return {
        ...item,
        amount: String(unitPrice * quantity),
        quantity: Number(quantity),
        unitPrice: String(unitPrice),
        product
      }
    })
  }

  let isSupplements: boolean | undefined

  if (PackageDetails.length > 0) {
    const packageItems = PackageDetails[PackageCount]?.packages_items

    if (packageItems.length > 0) {
      isSupplements = packageItems.every(
        (item: any) =>
          item?.class_id === 6 && !BRANDS_WITH_REQUIRED_FREIGHT.includes(item?.brand_id || item?.product?.brand_id)
      )
    }
  } else {
    isSupplements = orderDetails?.items.every(
      (item: any) =>
        item?.product.class_id === 6 &&
        !BRANDS_WITH_REQUIRED_FREIGHT.includes(item?.product?.brand_id || item?.brand_id)
    )
  }

  const validActiveName = (item: any, index: any) => {
    if (index === activeStepIndex && orderStatus.translated_name !== 'Entregue') {
      if (orderStatus.translated_name === 'Enviado') {
        return 'Pacote enviado'
      }

      if (orderStatus.translated_name === 'Aguardando Documentos') {
        return 'Aguardando documentos'
      }

      return item.activeName
    }
    if (item.checked) {
      return item.checkedName
    }

    return item.name
  }

  const getHeight = () => {
    if (!charges) return '60px'

    if (charges.length === 0) {
      return '60px'
    }

    if (charges.length === 1) {
      return '190px'
    }
    if (charges.length === 2) {
      return '295px'
    }
    return '60px'
  }

  return (
    <S.ContainerContent isSmallScreen={isSmallScreen}>
      <div style={{ display: isSmallScreen ? 'none' : 'relative' }} className="button items-center py-2">
        <Button onClick={() => history.push('/conta?pagina=pedidos-todos')}>Voltar para a lista de pedidos</Button>
      </div>
      <S.spacingDiv isSmallScreen={isSmallScreen}>
        <Heading defaultColor="darkGrey" fontSize="24px" style={{ marginBottom: '24px' }}>
          Resumo da compra
        </Heading>
      </S.spacingDiv>
      <S.spacingDiv isSmallScreen={isSmallScreen}>
        <Heading defaultColor="darkGrey" fontSize="16px" style={{ marginBottom: '8px' }}>
          N° do Pedido <span style={{ color: '#0D855C' }}>{orderDetails?.id}</span>
        </Heading>
      </S.spacingDiv>
      {(() => {
        const geneticTestDocs = (orderDetails?.documents || [])
          .flat()
          .filter((doc: any) => doc.type === 'TESTE_GENETICO')
        const uniqueDocs = geneticTestDocs.filter(
          (doc: any, index: number, self: any[]) => self.findIndex((d: any) => d.id === doc.id) === index
        )
        if (uniqueDocs.length === 0) return null
        return (
          <S.spacingDiv isSmallScreen={isSmallScreen}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <p style={{ fontWeight: 600, fontSize: '14px', color: '#374151' }}>Resultado do Teste Genético</p>
              {uniqueDocs.map((doc: any, index: number) => (
                <a
                  key={doc.id || index}
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #d1fae5',
                    backgroundColor: '#f0fdf4',
                    color: '#065f46',
                    fontSize: '14px',
                    textDecoration: 'none'
                  }}>
                  <BsFileEarmarkText size={18} style={{ flexShrink: 0 }} />
                  <span>{doc.originalName || `Teste Genético${uniqueDocs.length > 1 ? ` ${index + 1}` : ''}`}</span>
                </a>
              ))}
            </div>
          </S.spacingDiv>
        )
      })()}
      <S.spacingDiv isSmallScreen={isSmallScreen}>
        {OrderPackage.length > 0 &&
          OrderPackage?.map((pack: PackagesData, index: number) => (
            <S.Button isActive={index === PackageCount} key={index} onClick={() => packageUpdate(index)}>
              Pacote {index + 1} - #{pack.id}
            </S.Button>
          ))}
        {OrderAwb.length > 0 &&
          OrderAwb?.map((pack: AwbsData, index: number) => (
            <S.Button isActive={index === PackageCount} key={index} onClick={() => packageUpdate(index)}>
              Pacote {index + 1}- #{pack.id}
            </S.Button>
          ))}
        <S.lineDivisor />
      </S.spacingDiv>
      {(OrderPackage.length > 0 && OrderPackage[PackageCount]?.carrier_name !== 'A definir' && (
        <S.spacingDiv isSmallScreen={isSmallScreen}>
          <S.Header>
            <BsTruck size={30} />
            <span>
              Transportadora: <strong>{OrderPackage[PackageCount]?.carrier_name}</strong>
            </span>
          </S.Header>
        </S.spacingDiv>
      )) ||
        (OrderAwb.length > 0 && OrderAwb[PackageCount]?.shippingCompany && (
          <S.spacingDiv isSmallScreen={isSmallScreen}>
            <S.Header>
              <BsTruck size={30} />
              <span>
                Transportadora: <strong>{OrderAwb[PackageCount]?.shippingCompany}</strong>
              </span>
            </S.Header>
          </S.spacingDiv>
        ))}

      {(OrderPackage.length > 0 || OrderAwb.length > 0) &&
        (OrderAwb[PackageCount]?.trackingNumber || OrderPackage[PackageCount]?.tracking_number) && (
          <S.spacingDiv isSmallScreen={isSmallScreen}>
            <S.Header>
              <BsTag size={30} />
              <span>
                Código de Rastreio:{' '}
                <strong>
                  {!OrderPackage
                    ? OrderAwb[PackageCount]?.trackingNumber || 'A definir'
                    : OrderPackage[PackageCount]?.tracking_number || 'A definir'}
                </strong>
              </span>
            </S.Header>
          </S.spacingDiv>
        )}

      <S.spacingDiv isSmallScreen={isSmallScreen}>
        <S.Header>
          <span>
            Ficou com alguma dúvida? Entre em contato conosco{' '}
            <S.LinkLabel href={linkAtendimento} target="_blank">
              clicando aqui!
            </S.LinkLabel>
          </span>
        </S.Header>
      </S.spacingDiv>

      <S.ContentTimeLine>
        {timeLineStatus.map((item: any, index: number) => {
          if (item.id === 1) {
            return (
              <S.Wrapper
                key={item.id}
                status={getStatusCss(item.checked, item.isVerified, index === activeStepIndex || !statusPayment)}
                isActive={index === activeStepIndex}>
                <div className="wrapper">
                  <div className="title">
                    <div className="icon">{item.icon}</div>
                    <div>
                      <p className="name">
                        {statusPayment?.status === 'paid' && <p className="label">Pagamento aprovado</p>}
                        {statusPayment?.status === 'payment_refused' && <p className="label">Pagamento recusado</p>}
                        {statusPayment?.status === 'waiting_payment' && <p className="label"> Aguardando pagamento</p>}
                        {!statusPayment && <p className="label"> Aguardando pagamento</p>}
                      </p>
                    </div>
                  </div>
                  <S.BadgeIcon className="badge">
                    {index === activeStepIndex && (
                      <span className="spin">
                        <LoadingOutlined spin />
                      </span>
                    )}
                  </S.BadgeIcon>
                </div>
                <div className="line" style={{ height: isDropdownOpen ? getHeight() : '60px' }} />
                <div className="title_label">
                  {statusPayment?.status === 'paid' && !loadingPayment && (
                    <>
                      <p className="label">
                        Pagamento aprovado no dia {format(new Date(statusPayment?.date), 'dd/MM/yyyy - HH:mm')}
                      </p>
                      {charges.length > 0 && (
                        <>
                          <S.DropdownButton onClick={() => setDropdownOpen(!isDropdownOpen)}>
                            {isDropdownOpen ? <>Ocultar detalhes do pagamento</> : <>Ver detalhes do pagamento</>}
                          </S.DropdownButton>
                          <PaymentDropdown
                            paymentInfo={paymentInfo}
                            isOpen={isDropdownOpen}
                            onClick={() => setDropdownOpen(!isDropdownOpen)}
                          />
                        </>
                      )}
                    </>
                  )}
                  {statusPayment?.status === 'payment_refused' && <p className="label">Pagamento recusado</p>}
                  {statusPayment?.status === 'waiting_payment' && <p className="label"> Aguardando pagamento</p>}
                  {!statusPayment && <p className="label">A aprovação do pagamento pode levar aguns minutos...</p>}
                </div>
              </S.Wrapper>
            )
          }

          if (item.id === 2 && isSupplements) {
            return null
          }

          if ((item.id === 6 || item.id === 7) && isDeliveredByAssociation) {
            return null
          }

          return (
            <S.Wrapper
              key={item.id}
              status={getStatusCss(
                item.checked,
                item.isVerified,
                !item.checked && index === activeStepIndex && statusPayment && statusPayment?.status === 'paid'
              )}
              isActive={
                !item.checked && index === activeStepIndex && statusPayment && statusPayment?.status === 'paid'
              }>
              <div className="wrapper">
                <div className="title">
                  <div className="icon">{item.icon}</div>
                  <div>
                    <p className="name">{validActiveName(item, index)}</p>
                  </div>
                </div>
                <S.BadgeIcon className="badge">
                  {!item.checked && index === activeStepIndex && (
                    <span className="spin">
                      <LoadingOutlined spin />
                    </span>
                  )}
                </S.BadgeIcon>
              </div>
              <div className="line" />
              <div className="title_label">
                {item.id === 2 && DocumentExcept && statusPayment?.status === 'paid' && (
                  <S.LinkLabel href={`/validacao-documentos/${orderDetails?.id}`} target="_blank">
                    Enviar documentos
                  </S.LinkLabel>
                )}
                {item.checked ? (
                  <p className="label">
                    {index === activeStepIndex && item.id === 8 && orderStatus?.translated_name === 'Entregue' && (
                      <span>
                        Seu pedido foi entregue no dia{' - '}
                        {format(
                          new Date(
                            Array.isArray(PackageDetails[PackageCount]?.statusLogs) &&
                              PackageDetails[PackageCount]?.statusLogs.length > 0
                              ? getLatestByUpdated(PackageDetails[PackageCount]?.statusLogs)?.updatedAt
                              : orderStatus?.date
                          ),
                          'dd/MM/yyyy - HH:mm'
                        )}
                      </span>
                    )}
                    {item.id !== 8 &&
                      item.message &&
                      item.statusCreatedAt &&
                      `${item.message} - ${format(new Date(item.statusCreatedAt), 'dd/MM/yyyy - HH:mm')}`}
                    {item.id !== 8 &&
                      !item.message &&
                      (() => {
                        const dateStr = item.statusCreatedAt || orderStatus?.date
                        if (!dateStr) return item.label
                        const d = new Date(dateStr)
                        if (isNaN(d.getTime())) return item.label
                        return `${item.label} - ${format(d, 'dd/MM/yyyy - HH:mm')}`
                      })()}
                  </p>
                ) : (
                  <p className="label">
                    {index === activeStepIndex && item.message && item.statusCreatedAt && (
                      <span>
                        {item.message} - {format(new Date(item.statusCreatedAt), 'dd/MM/yyyy - HH:mm')}
                      </span>
                    )}
                    {index === activeStepIndex &&
                      !item.message &&
                      item.id === 4 &&
                      orderStatus?.translated_name === 'Documentos Aprovados' &&
                      'Aguardando envio'}
                    {index === activeStepIndex &&
                      !item.message &&
                      orderStatus?.translated_name !== 'Documentos Aprovados' &&
                      orderStatus?.translated_name !== 'Entregue' &&
                      StatusMessage}
                  </p>
                )}
              </div>
            </S.Wrapper>
          )
        })}
      </S.ContentTimeLine>
      <ProductList
        isNotEditingFlavor
        isNotEditing
        productList={filteredItems?.length > 0 ? filteredItems : orderDetails?.items}
        style={{
          marginTop: '32px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}
      />
      <Resume isNotEditing resumeDetails={orderDetails} />
      <div style={{ visibility: isSmallScreen ? 'visible' : 'hidden' }} className="button items-center py-2">
        <Button
          onClick={() => history.push('/conta?pagina=pedidos-todos')}
          style={{
            backgroundColor: '#0D855C',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '4px'
          }}>
          Voltar para a lista de pedidos
        </Button>
      </div>
    </S.ContainerContent>
  )
}

export default AnalyzeCannect
